'use client';

import { Search } from 'lucide-react';
import { useLocale, useTranslations } from 'next-intl';
import { usePathname } from 'next/navigation';
import React, { useEffect, useState } from 'react';

import { NavButton } from '@ui/components/common/NavButton';
import { Button } from '@ui/components/common/button';
import { useIsMobile } from '@website/hooks/useIsMobile';

interface AuthActionsProps {
  loginHref: string;
}

/**
 * Renders authentication-related actions: search link (desktop: label + icon, mobile: icon only) and login button.
 * Uses mounted state to prevent early click on '#' href before hydration.
 * The loginHref may be '#' during SSR (from getLoginHrefClient) and gets
 * the actual URL after hydration.
 */
export const AuthActions: React.FC<AuthActionsProps> = ({ loginHref }) => {
  const t = useTranslations('Auth');
  const tNav = useTranslations('Navigation');
  const locale = useLocale();
  const pathname = usePathname();
  const isMobile = useIsMobile();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleLoginClick = () => {
    if (mounted && loginHref && loginHref !== '#') {
      window.location.href = loginHref;
    }
  };

  const searchHref = `/${locale}/search`;
  const isOnSearchPage = pathname === searchHref || pathname.startsWith(`${searchHref}/`);

  return (
    <div className="flex items-center gap-2 h-6">
      {!isOnSearchPage && (
        <NavButton
          href={searchHref}
          currentPathname={pathname}
          tooltip={tNav('search')}
          // `prefetch={false}` on this link specifically — the search page
          // transitively pulls Luxon (date filters) and Zod (search-input
          // schema), and Next App Router prefetches Link targets on viewport
          // entry by default. Anonymous viewers landing on a public detail
          // page were paying ~50 KiB of search-page chunks they may never use.
          prefetch={false}
          className={!isMobile ? 'w-auto min-w-11 px-3' : undefined}
        >
          <Search className="size-7" strokeWidth={1.5} />
          {!isMobile && <span>{tNav('search')}</span>}
        </NavButton>
      )}
      <Button label={t('login')} variant="primary" onClick={handleLoginClick} aria-busy={!mounted} />
    </div>
  );
};
